import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import {
  sortCzech,
  type AdminDestinationListItem,
  type AdminInspirationListItem,
} from "@ckgo2/shared";
import { api, type ApiResponse } from "@/api/client";

/**
 * Sdílené dotazy na admin seznamy destinací a inspirací ve štíhlé projekci (vize F9.3, řez 09).
 * Jediné místo s cestami těchto seznamů: seznamy, roletky i editory čtou přes tyto hooky,
 * takže sdílejí jednu keš a nikdo nestahuje plné řádky (plný seznam destinací má přes 14 MB).
 * Výjimka mimo tento soubor: `components/googleReviews/useDestinationOptions.ts` (veřejná
 * štíhlá projekce jen publikovaných destinací pro Google recenze).
 *
 * Klíče začínají `listKey` editorů (`"admin-destinations"`, `"activities"`), takže invalidace
 * `useEditPage` podle prefixu po uložení zasáhne i tyto seznamy. Cesty stojí jako celé
 * literály s `fields=slim`, aby je měřidlo `kontrola:admin-seznamy` četlo bez výjimky.
 *
 * Pořadí: čtenáři dostávají seznam česky abecedně (`compareCzech` podle `name`, resp. `title`;
 * F1.8) přes `select` dotazu, takže roletky, štítky a lookupy bez vlastního řazení nevidí
 * pořadí API. Keš drží odpověď API beze změny; čtenáři s vlastním řazením si ho udělají sami.
 */
export const ADMIN_DESTINATION_LIST_KEY = ["admin-destinations", "slim"] as const;
export const ADMIN_INSPIRATION_LIST_KEY = ["activities", "slim"] as const;

export interface AdminListQueryOptions {
  enabled?: boolean;
  staleTime?: number;
}

/**
 * `select` sdíleného seznamu destinací. Modulová konstanta, ne lambda v hooku: TanStack Query
 * memoizuje výsledek `select` jen při stabilní referenci funkce, jinak by se ~1 000 destinací
 * přeřadilo při každém renderu každého čtenáře.
 */
export function sortDestinationListByName(
  res: ApiResponse<AdminDestinationListItem[]>,
): ApiResponse<AdminDestinationListItem[]> {
  return { ...res, data: sortCzech(res.data ?? [], (d) => d.name) };
}

/** `select` sdíleného seznamu inspirací (modulová konstanta, viz výše). */
export function sortInspirationListByTitle(
  res: ApiResponse<AdminInspirationListItem[]>,
): ApiResponse<AdminInspirationListItem[]> {
  return { ...res, data: sortCzech(res.data ?? [], (a) => a.title) };
}

/**
 * Jen zadané volby: `staleTime: undefined` by v TanStack Query přebilo globální výchozí 5 min
 * z `main.tsx` (výchozí volby se slévají spreadem), proto se nezadané klíče vůbec nepředají.
 * Klíč dotazu na volbách nezávisí.
 */
function definedOptions(opts: AdminListQueryOptions | undefined): AdminListQueryOptions {
  const out: AdminListQueryOptions = {};
  if (opts?.enabled !== undefined) out.enabled = opts.enabled;
  if (opts?.staleTime !== undefined) out.staleTime = opts.staleTime;
  return out;
}

export function useAdminDestinationList(
  opts?: AdminListQueryOptions,
): UseQueryResult<ApiResponse<AdminDestinationListItem[]>> {
  return useQuery({
    queryKey: ADMIN_DESTINATION_LIST_KEY,
    queryFn: () =>
      api.get<ApiResponse<AdminDestinationListItem[]>>("/api/admin/destinations?fields=slim"),
    select: sortDestinationListByName,
    ...definedOptions(opts),
  });
}

export function useAdminInspirationList(
  opts?: AdminListQueryOptions,
): UseQueryResult<ApiResponse<AdminInspirationListItem[]>> {
  return useQuery({
    queryKey: ADMIN_INSPIRATION_LIST_KEY,
    queryFn: () =>
      api.get<ApiResponse<AdminInspirationListItem[]>>("/api/admin/inspirations?fields=slim"),
    select: sortInspirationListByTitle,
    ...definedOptions(opts),
  });
}
